import React from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router-dom";

import {selectCurrentRoutePermissions} from "../../../../../slices/PersonSlice";
import {selectIsItManager} from "../../../../../slices/UserSlice";

const useRoutePermissions = ({uiPermissionType}) => {
  const location = useLocation();
  const pathName = location && location?.pathname?.slice(6);
  const currentRoutePermissions = useSelector(state => selectCurrentRoutePermissions(state, pathName));
  const isItManager = useSelector(selectIsItManager);

  const isDisabledByRoutePermission =
    (uiPermissionType === UI_PERMISSION_TYPE.IT_DEV_ONLY && !isItManager) ||
    (uiPermissionType !== UI_PERMISSION_TYPE.IT_DEV_ONLY &&
      currentRoutePermissions &&
      uiPermissionType !== UI_PERMISSION_TYPE.READABLE &&
      currentRoutePermissions[ROUTE_PERMISSIONS_INDEX[uiPermissionType] || 100] !== "1");

  return {isDisabledByRoutePermission};
};

export default useRoutePermissions;

export const UI_PERMISSION_TYPE = Object.freeze({
  READABLE: "READABLE",
  WRITABLE: "WRITABLE",
  CONTROLABLE: "CONTROLABLE",
  IT_DEV_ONLY: "IT_DEV_ONLY",
});

export const ROUTE_PERMISSIONS_INDEX = Object.freeze({
  [UI_PERMISSION_TYPE.READABLE]: 0,
  [UI_PERMISSION_TYPE.WRITABLE]: 1,
  [UI_PERMISSION_TYPE.CONTROLABLE]: 2,
});

export const formChildrenWithPermissions = ({children, uiPermissionType}) => {
  const addPermissionsRecursively = child => {
    // Return non-React elements (strings, numbers, null, undefined) as-is
    if (!React.isValidElement(child)) return child;

    // Check if the element is a custom React component (function or class)
    // vs. a standard DOM element like 'div', 'button', etc.
    const isCustomComponent = typeof child.type !== "string";

    // Recursively process nested children (inner children, inner-inner children, etc.)
    const processChildren = node => {
      if (!node) return node;
      return React.Children.map(node, addPermissionsRecursively);
    };

    const newChildren = processChildren(child.props?.children);

    // Prepare new props
    const newProps = {
      ...(isCustomComponent && {uiPermissionType}),
    };

    // Clone element with updated props and recursively processed children
    return React.cloneElement(child, newProps, newChildren);
  };

  return React.Children.map(children, addPermissionsRecursively);
};
